// Authentication utility functions
import { getClaimsFromToken } from "./jwtDecoder";
import { getLoginHref } from "./appPaths";

const SESSION_EXPIRES_AT_KEY = "csrp.session.expiresAt";

/**
 * Browser session window for warnings / client-side guard (must match API cookie expiry).
 * Set `REACT_APP_SESSION_TIMEOUT_MINUTES` in `.env` to match `Session:TimeoutMinutes` in appsettings (default 120).
 * This does NOT shut down the IIS API; it only controls when the user must sign in again.
 */
const SESSION_TIMEOUT_MINUTES = (() => {
  const raw = process.env.REACT_APP_SESSION_TIMEOUT_MINUTES;
  const n = raw != null && String(raw).trim() !== "" ? parseInt(String(raw), 10) : 120;
  return Number.isFinite(n) && n > 0 ? n : 120;
})();

export const SESSION_DURATION_MS = SESSION_TIMEOUT_MINUTES * 60 * 1000;
/** Show warning modal this many ms before expiry. */
export const SESSION_WARNING_BEFORE_MS = 5 * 60 * 1000;
const REFRESH_TOKEN_KEY = "refreshToken";

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export const getSessionExpiresAt = () => {
  try {
    const raw = localStorage.getItem(SESSION_EXPIRES_AT_KEY);
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

export const setSessionExpiresAt = (expiresAtMs) => {
  const n = Number(expiresAtMs);
  if (!Number.isFinite(n) || n <= 0) return;
  safeSetItem(SESSION_EXPIRES_AT_KEY, String(Math.floor(n)));
};

/** Milliseconds until local session expiry, or null if unknown. */
export const getMsUntilSessionExpiry = () => {
  const expiresAt = getSessionExpiresAt();
  if (expiresAt == null) return null;
  return Math.max(0, expiresAt - Date.now());
};

/** Sync local expiry from server keep-alive (seconds remaining). */
export const extendSessionFromServer = (sessionExpiresInSeconds) => {
  const sec = Number(sessionExpiresInSeconds);
  if (!Number.isFinite(sec) || sec <= 0) return;
  setSessionExpiresAt(Date.now() + sec * 1000);
};

/** Extend local session by the default window (after login or manual continue). */
export const extendSession = () => {
  setSessionExpiresAt(Date.now() + SESSION_DURATION_MS);
};

// Initialize session timestamp when user exists but expiry is missing (legacy logins).
export const touchSession = () => {
  const existing = getSessionExpiresAt();
  if (existing != null) return;
  const userStr = (() => {
    try {
      return localStorage.getItem("user");
    } catch {
      return null;
    }
  })();
  if (!userStr) return;
  extendSession();
};

export const clearSession = () => {
  safeRemoveItem(SESSION_EXPIRES_AT_KEY);
};

export const setRefreshToken = (refreshToken) => {
  if (refreshToken) {
    safeSetItem(REFRESH_TOKEN_KEY, String(refreshToken));
  } else {
    safeRemoveItem(REFRESH_TOKEN_KEY);
  }
};

export const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem("token", token);
    // TODO: Privileges will be implemented later
    // Extract and store privileges from token
    const claims = getClaimsFromToken(token);
    // if (claims && claims.privileges) {
    //   localStorage.setItem('privileges', JSON.stringify(claims.privileges));
    // }
    if (claims && claims.roles) {
      localStorage.setItem("roles", JSON.stringify(claims.roles));
    }
  } else {
    localStorage.removeItem("token");
    // TODO: Privileges will be implemented later
    // localStorage.removeItem('privileges');
    localStorage.removeItem("roles");
  }
};

export const getAuthToken = () => {
  return localStorage.getItem("token");
};

export const setUser = (user) => {
  if (user) {
    localStorage.setItem("user", JSON.stringify(user));
    touchSession();
  } else {
    localStorage.removeItem("user");
    clearSession();
  }
};

export const getUser = () => {
  const userStr = localStorage.getItem("user");
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Get user privileges from user object (Privileges array contains MenuCodes)
 * @returns {Array} Array of privilege codes (MenuCodes)
 */
export const getUserPrivileges = () => {
  const user = getUser();
  if (user) {
    // Check for Privileges array in user object
    const privileges = user.Privileges || user.privileges;
    if (Array.isArray(privileges) && privileges.length > 0) {
      return privileges;
    }
  }

  // Fallback to localStorage privileges
  const privilegesStr = localStorage.getItem('privileges');
  if (privilegesStr) {
    try {
      return JSON.parse(privilegesStr);
    } catch (e) {
      return [];
    }
  }

  return [];
};

/**
 * Get user roles from token, localStorage, or user object
 * @returns {Array} Array of role objects or role codes
 */
export const getUserRoles = () => {
  const token = getAuthToken();
  if (token) {
    const claims = getClaimsFromToken(token);
    if (claims && claims.roles) {
      return claims.roles;
    }
  }

  // Fallback to localStorage roles
  const rolesStr = localStorage.getItem("roles");
  if (rolesStr) {
    try {
      return JSON.parse(rolesStr);
    } catch (e) {
      // Continue to check user object
    }
  }

  // Fallback to user object's Roles property
  const user = getUser();
  if (user && user.Roles && Array.isArray(user.Roles)) {
    return user.Roles;
  }

  return [];
};

/**
 * Check if user has a specific role
 * @param {string} roleCode - Role code to check (e.g., "av", "se")
 * @returns {boolean} True if user has the role
 */
export const hasRole = (roleCode) => {
  if (!roleCode) return false;

  const roles = getUserRoles();
  if (!roles || roles.length === 0) return false;

  const roleCodeLower = roleCode.toLowerCase();

  return roles.some((role) => {
    if (!role) return false;

    // If role is a string
    if (typeof role === "string") {
      return role.toLowerCase() === roleCodeLower;
    }

    // If role is an object with RoleCode/RoleName
    if (typeof role === "object") {
      const code =
        (role.RoleCode ||
          role.roleCode ||
          role.RoleName ||
          role.roleName ||
          "") + "";
      return code.toLowerCase() === roleCodeLower;
    }

    return false;
  });
};

/**
 * Check if user has a specific UserTypeCode
 * @param {string} userTypeCode - UserTypeCode to check (e.g., "SE", "BCM", "ADMIN")
 * @returns {boolean} True if user has the UserTypeCode
 */
export const hasUserType = (userTypeCode) => {
  if (!userTypeCode) return false;

  const user = getUser();
  if (!user) return false;

  const userType =
    user.UserTypeCode ||
    user.userTypeCode ||
    user.UserType ||
    user.userType ||
    "";

  return userType.toLowerCase() === userTypeCode.toLowerCase();
};

/**
 * Service Executive — login may use numeric UserTypeCode (UT0000001) or short code "SE".
 * Use to hide approver-only UI (Approve/Reject/Close, material Action column, etc.).
 */
export const isSeUserType = () =>
  hasUserType("UT0000001") || hasUserType("SE");

/**
 * True if the logged-in user is BCM (editable factory intent / settlement on approve).
 * Uses UserTypeCode, role "bcm", and UserTypeName so different login payloads still match.
 */
/** Root Cause Analysis approver (UserTypeCode UT0000006). */
export const isRcaUserType = () => hasUserType("UT0000006");

/** Channel Head / CH (UserTypeCode UT0000009) — CH Kitty question on Special Case / Reopen. */
export const isChUserType = () => hasUserType("UT0000009");

/** Zonal Product Specialist (UserTypeCode UT0000003) — technical visit questions on Approve. */
export const isZonalProductSpecialistUserType = () => hasUserType("UT0000003");

export const isBcmUser = () => {
  if (hasUserType("BCM")) return true;
  if (hasRole("bcm")) return true;
  if (hasRole("Branch Commercial Manager")) return true;
  const user = getUser();
  if (!user) return false;
  const utn = String(user.UserTypeName || user.userTypeName || "").trim();
  if (/branch\s*commercial\s*manager/i.test(utn)) return true;
  if (/\bbcm\b/i.test(utn)) return true;
  return false;
};

/** DOS Admin / domestic coordinator (UserTypeCode UT0000024) — not system admin. */
export const isDosAdminUser = (user = getUser()) => {
  if (!user) return false;
  const userTypeCode = String(user.UserTypeCode || user.userTypeCode || "")
    .trim()
    .toUpperCase();
  const userTypeName = String(
    user.UserTypeName || user.userTypeName || user.Designation || user.designation || ""
  )
    .trim()
    .toUpperCase();
  const userName = String(user.UserName || user.userName || user.Username || user.username || "")
    .trim()
    .toLowerCase();
  const fullName = String(user.FullName || user.fullName || "")
    .trim()
    .toUpperCase();

  return (
    userTypeCode === "UT0000024" ||
    userTypeName === "DOS ADMIN" ||
    /^DOS\s+ADMIN$/.test(userTypeName) ||
    fullName === "DOS ADMIN" ||
    userName === "dos.admin"
  );
};

/** Zonal Head (UserTypeCode UT0000004) — Export Assign flow (like Domestic DOS Admin). */
export const isZonalHeadUser = (user = getUser()) => {
  if (!user) return false;
  const userTypeCode = String(user.UserTypeCode || user.userTypeCode || "")
    .trim()
    .toUpperCase();
  const userTypeName = String(
    user.UserTypeName || user.userTypeName || user.Designation || user.designation || ""
  )
    .trim()
    .toUpperCase();
  return (
    userTypeCode === "UT0000004" ||
    userTypeName === "ZONAL HEAD" ||
    /^ZONAL\s+HEAD$/.test(userTypeName)
  );
};

/** System admin (UT0000017 / Admin Role) — not DOS Admin (UT0000024). */
export const isSystemAdminUser = (user = getUser()) => {
  if (!user) return false;

  const userTypeCode = String(user.UserTypeCode || user.userTypeCode || "")
    .trim()
    .toUpperCase();
  const userTypeName = String(
    user.UserTypeName || user.userTypeName || user.Designation || user.designation || ""
  )
    .trim()
    .toUpperCase();
  const roleCodes = getUserRoles()
    .map((role) =>
      String(role?.RoleCode || role?.roleCode || role?.RoleName || role?.roleName || role || "")
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);

  const isDosAdmin = isDosAdminUser(user);
  const hasAdminType =
    !isDosAdmin &&
    (userTypeCode === "ADMIN" ||
      userTypeCode === "SYSADMIN001" ||
      userTypeCode === "UT0000017" ||
      userTypeName === "ADMIN ROLE" ||
      userTypeName.includes("ADMINISTRATOR"));
  const hasAdminRole = roleCodes.includes("ADMIN") || roleCodes.includes("ADMINISTRATOR");
  const hasAdminPrivilege = getUserPrivileges()
    .map((x) => String(x || "").trim().toUpperCase())
    .includes("ADMIN_SERVICE_REQUEST_MANAGE");
  const hasAdminPrivilegeDetails = getUserPrivilegeDetails().some((p) => {
    const code = String(p?.MenuCode || p?.menuCode || "").trim().toUpperCase();
    const canView = p?.ViewPermission === true || p?.viewPermission === true;
    return canView && code === "ADMIN_SERVICE_REQUEST_MANAGE";
  });

  return hasAdminType || hasAdminRole || hasAdminPrivilege || hasAdminPrivilegeDetails;
};

/**
 * Get user privilege details with permission flags from UserPrivilege table
 * @returns {Array} Array of privilege objects with MenuCode and permission flags
 */
export const getUserPrivilegeDetails = () => {
  const user = getUser();
  if (user) {
    // Check for PrivilegeDetails array in user object (new format with permission flags)
    const privilegeDetails = user.PrivilegeDetails || user.privilegeDetails;
    if (Array.isArray(privilegeDetails) && privilegeDetails.length > 0) {
      return privilegeDetails;
    }
    
    // Debug: Log if PrivilegeDetails is missing
    if (process.env.NODE_ENV === 'development') {
      console.log('User object:', user);
      console.log('PrivilegeDetails:', privilegeDetails);
      console.log('Privileges (legacy):', user.Privileges || user.privileges);
    }
  }
  // Fallback: privilege details cached in localStorage by Sidebar bootstrap.
  try {
    const raw = localStorage.getItem("csrp.privilegeDetails");
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Check if user has a specific permission for a MenuCode
 * @param {string} menuCode - Menu code to check
 * @param {string} permissionType - Permission type: 'VIEW', 'ADD', 'EDIT', 'DELETE', 'DOWNLOAD'
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (menuCode, permissionType) => {
  if (!menuCode || !permissionType) return false;
  
  const privilegeDetails = getUserPrivilegeDetails();
  const privilege = privilegeDetails.find(
    (p) => p.MenuCode === menuCode || p.menuCode === menuCode
  );
  
  if (!privilege) return false;
  
  const permissionTypeUpper = permissionType.toUpperCase();
  switch (permissionTypeUpper) {
    case 'VIEW':
      return privilege.ViewPermission === true || privilege.viewPermission === true;
    case 'ADD':
      return privilege.AddPermission === true || privilege.addPermission === true;
    case 'EDIT':
      return privilege.EditPermission === true || privilege.editPermission === true;
    case 'DELETE':
      return privilege.DeletePermission === true || privilege.deletePermission === true;
    case 'DOWNLOAD':
      return privilege.DownloadPermission === true || privilege.downloadPermission === true;
    default:
      return false;
  }
};

/**
 * Check if user has a permission for ANY of the provided MenuCodes.
 * Useful when a route maps to multiple MenuCodes (e.g., VIEW and MANAGE codes).
 * @param {Array<string>} menuCodes
 * @param {string} permissionType - 'VIEW', 'ADD', 'EDIT', 'DELETE', 'DOWNLOAD'
 * @returns {boolean}
 */
export const hasAnyMenuPermission = (menuCodes, permissionType) => {
  if (!Array.isArray(menuCodes) || menuCodes.length === 0) return false;
  return menuCodes.some((code) => hasPermission(code, permissionType));
};

/**
 * Combine permissions across multiple MenuCodes (logical OR).
 * @param {Array<string>} menuCodes
 * @returns {{canView:boolean, canAdd:boolean, canEdit:boolean, canDelete:boolean}}
 */
export const getPermissionsForMenuCodes = (menuCodes) => {
  if (!Array.isArray(menuCodes) || menuCodes.length === 0) {
    return { canView: false, canAdd: false, canEdit: false, canDelete: false };
  }

  return {
    canView: hasAnyMenuPermission(menuCodes, "VIEW"),
    canAdd: hasAnyMenuPermission(menuCodes, "ADD"),
    canEdit: hasAnyMenuPermission(menuCodes, "EDIT"),
    canDelete: hasAnyMenuPermission(menuCodes, "DELETE"),
  };
};

const getUserMenuCodesByPath = (path) => {
  try {
    const raw = localStorage.getItem("csrp.userMenus");
    const list = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(list) || list.length === 0) return [];

    const flatten = (arr, out = []) => {
      for (const m of arr) {
        if (!m) continue;
        out.push(m);
        const kids = m.Children || m.children;
        if (Array.isArray(kids) && kids.length > 0) flatten(kids, out);
      }
      return out;
    };

    const flat = flatten(list, []);
    const match = flat.find((m) => {
      const pageName = String(m.PageName || m.pageName || "").trim();
      if (!pageName) return false;
      if (pageName === path) return true;
      return path.startsWith(pageName + "/");
    });

    const code = match ? match.MenuCode || match.menuCode : null;
    return code ? [code] : [];
  } catch {
    return [];
  }
};

/**
 * Check if user has a specific privilege (MenuCode)
 * @param {string} menuCode - Menu code to check
 * @returns {boolean} True if user has the privilege
 */
export const hasPrivilege = (menuCode) => {
  if (!menuCode) return false;
  
  // First check using permission flags (new method)
  if (hasPermission(menuCode, 'VIEW')) {
    return true;
  }
  
  // Fallback to legacy method (check if MenuCode exists in privileges array)
  const privileges = getUserPrivileges();
  return privileges.includes(menuCode);
};

/**
 * Check permissions for current route dynamically
 * Gets MenuCode from current path and checks permissions
 * @param {string} path - Current route path (optional, will use window.location if not provided)
 * @param {string} permissionType - Permission type: 'VIEW', 'ADD', 'EDIT', 'DELETE'
 * @param {Function} getMenuCodeByPathFn - Function to get MenuCode from path (to avoid circular dependency)
 * @returns {boolean} True if user has the permission
 */
export const hasRoutePermission = (path = null, permissionType = 'VIEW', getMenuCodeByPathFn = null) => {
  // Get current path if not provided
  if (!path && typeof window !== 'undefined') {
    path = window.location.pathname;
  }
  
  if (!path) return false;
  
  // Get MenuCodes from path (route can map to multiple codes)
  let menuCodes = [];
  if (getMenuCodeByPathFn) {
    // Backward compatible: caller might pass getMenuCodeByPath, so wrap into array.
    const maybeSingle = getMenuCodeByPathFn(path);
    menuCodes = maybeSingle ? [maybeSingle] : [];
  } else {
    // Backend-driven: resolve MenuCode by matching PageName in user menus.
    menuCodes = getUserMenuCodesByPath(path);
  }

  if (!menuCodes || menuCodes.length === 0) {
    // If no MenuCode found for this route, allow access (for public routes)
    return true;
  }

  return hasAnyMenuPermission(menuCodes, permissionType);
};

/**
 * Get all permissions for current route dynamically
 * @param {string} path - Current route path (optional)
 * @param {Function} getMenuCodeByPathFn - Function to get MenuCode from path (to avoid circular dependency)
 * @returns {Object} Object with canView, canAdd, canEdit, canDelete
 */
export const getRoutePermissions = (path = null, getMenuCodeByPathFn = null) => {
  // Get current path if not provided
  if (!path && typeof window !== 'undefined') {
    path = window.location.pathname;
  }
  
  if (!path) {
    return { canView: false, canAdd: false, canEdit: false, canDelete: false };
  }
  
  // Get MenuCodes from path (route can map to multiple codes)
  let menuCodes = [];
  if (getMenuCodeByPathFn) {
    // Backward compatible: caller might pass getMenuCodeByPath, so wrap into array.
    const maybeSingle = getMenuCodeByPathFn(path);
    menuCodes = maybeSingle ? [maybeSingle] : [];
  } else {
    // Backend-driven: resolve MenuCode by matching PageName in user menus.
    menuCodes = getUserMenuCodesByPath(path);
  }

  if (!menuCodes || menuCodes.length === 0) {
    // If no MenuCode found for this route, allow all (for public routes)
    return { canView: true, canAdd: true, canEdit: true, canDelete: true };
  }

  return getPermissionsForMenuCodes(menuCodes);
};

/**
 * Check if user has any of the specified privileges
 * @param {Array} privilegeCodes - Array of privilege codes (MenuCodes)
 * @returns {boolean} True if user has at least one privilege
 */
export const hasAnyPrivilege = (privilegeCodes) => {
  if (!Array.isArray(privilegeCodes) || privilegeCodes.length === 0) return false;
  const privileges = getUserPrivileges();
  return privilegeCodes.some(code => privileges.includes(code));
};

/**
 * Check if user has all of the specified privileges
 * @param {Array} privilegeCodes - Array of privilege codes (MenuCodes)
 * @returns {boolean} True if user has all privileges
 */
export const hasAllPrivileges = (privilegeCodes) => {
  if (!Array.isArray(privilegeCodes) || privilegeCodes.length === 0) return false;
  const privileges = getUserPrivileges();
  return privilegeCodes.every(code => privileges.includes(code));
};
// export const hasAllPrivileges = (privilegeCodes) => {
//   if (!Array.isArray(privilegeCodes)) return false;
//   const privileges = getUserPrivileges();
//   return privilegeCodes.every(code => privileges.includes(code));
// };

export const isAuthenticated = () => {
  const expiresAt = getSessionExpiresAt();
  if (expiresAt != null && Date.now() > expiresAt) {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("roles");
    } catch {
      // ignore
    }
    clearSession();
    return false;
  }

  // Check for token first (if token-based auth is used)
  const token = getAuthToken();
  if (token) {
    return true;
  }

  // If no token, check for user data (for tokenless authentication)
  // This handles cases where backend doesn't provide tokens
  const user = getUser();
  const ok = !!user;
  // If we have a user but no session timestamp yet (older logins), initialize it.
  if (ok && expiresAt == null) touchSession();
  return ok;
};

/** HO Commercial / Export Comm. Controller — last-level closer on approver dashboard. */
export const isFinalCloserUserType = (user = getUser()) => {
  if (!user) return false;
  const userTypeCode = String(user.UserTypeCode || user.userTypeCode || "")
    .trim()
    .toUpperCase();
  const userTypeName = String(
    user.UserTypeName || user.userTypeName || user.Designation || user.designation || ""
  )
    .trim()
    .toUpperCase();
  if (userTypeCode === "UT0000013" || userTypeCode === "UT0000021") return true;
  if (userTypeName === "HO COMMERCIAL" || /^HO\s+COMM/.test(userTypeName)) return true;
  if (
    userTypeName.includes("COMM. CONTROLLER") ||
    userTypeName.includes("COMM CONTROLLER") ||
    userTypeName.includes("EXPORT COMM. CONTROLLER") ||
    userTypeName.includes("EXPORT COMM CONTROLLER")
  ) {
    return true;
  }
  if (/\bCLOSER\b/.test(userTypeName) || userTypeName.includes("LAST LEVEL")) return true;
  return false;
};

export const logout = () => {
  // Clear all authentication data
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  // TODO: Privileges will be implemented later
  // localStorage.removeItem('privileges');
  localStorage.removeItem("roles");
  setRefreshToken(null);
  clearSession();

  window.location.href = getLoginHref();
};
